import {useEffect, useState, useCallback} from 'react';
import {api} from '../lib/auth.js';

/**
 * Edit a customer, supplier or article in the live Fortnox register.
 *
 * Only CHANGED fields are sent. Fortnox rejects a PUT carrying read-only fields, and
 * sending the whole record back would also silently overwrite anything another user
 * changed in between — a last-write-wins that nobody asked for.
 *
 * The changed fields are listed before saving, because this writes to the real
 * accounting register and "what am I about to change" should not require trust.
 *
 * A record that exists only in Jabr — which is every article in the article register — is
 * not a dead end. Fortnox returns 404, and the drawer then offers to CREATE it there,
 * showing exactly the payload that would be sent. That is the whole reason the register
 * was unusable before: it was designed here and had never been pushed.
 *
 * "Ta bort" DEACTIVATES. Fortnox refuses to delete anything an invoice references, so a
 * hard delete fails on precisely the records with history, and it would leave the
 * classification rows pointing at an article number that no longer exists.
 */
const LABELS = {
	Name: 'Namn', OrganisationNumber: 'Orgnr', Email: 'E-post', Phone1: 'Telefon',
	Address1: 'Adress', ZipCode: 'Postnr', City: 'Ort', CountryCode: 'Landskod',
	Currency: 'Valuta', VATNumber: 'Momsnr', TermsOfPayment: 'Betalvillkor',
	Active: 'Aktiv', Comments: 'Kommentar', Description: 'Benämning',
	SalesPrice: 'Försäljningspris', SalesAccount: 'Försäljningskonto', Unit: 'Enhet',
};

export default function EditDrawer({resource, id, onClose, onSaved}) {
	const [data, setData] = useState(null);
	const [error, setError] = useState(null);
	const [draft, setDraft] = useState({});
	const [saving, setSaving] = useState(false);
	const [result, setResult] = useState(null);
	const [missing, setMissing] = useState(null);
	const [confirmOff, setConfirmOff] = useState(false);

	const load = useCallback(() => {
		setError(null); setMissing(null); setData(null);
		api(`/api/edit/${resource}/${encodeURIComponent(id)}`)
			.then(async r => {
				const j = await r.json();
				// 404 is the meaningful case, not a failure: the record lives only in Jabr and
				// can be created in Fortnox from what we already hold.
				if (r.status === 404 && j.missing_in_fortnox) { setMissing(j); return null; }
				if (!r.ok) throw new Error(j.error);
				return j;
			})
			.then(d => d && setData(d))
			.catch(e => setError(e.message));
	}, [resource, id]);
	useEffect(() => { load(); }, [load]);

	const create = async () => {
		setSaving(true); setResult(null);
		try {
			const res = await api(`/api/edit/${resource}`, {
				method: 'POST', headers: {'Content-Type': 'application/json'},
				body: JSON.stringify({id, fields: missing?.would_send ?? {}}),
			});
			const j = await res.json();
			if (!res.ok) { setResult({error: j.error}); return; }
			setResult({created: j.id});
			load();
			onSaved?.();
		} catch (e) { setResult({error: e.message}); } finally { setSaving(false); }
	};

	const deactivate = async () => {
		setSaving(true); setResult(null);
		try {
			const res = await api(`/api/edit/${resource}/${encodeURIComponent(id)}`, {method: 'DELETE'});
			const j = await res.json();
			if (!res.ok) { setResult({error: j.error}); return; }
			setResult({deactivated: true});
			setConfirmOff(false);
			load();
			onSaved?.();
		} catch (e) { setResult({error: e.message}); } finally { setSaving(false); }
	};

	const changed = Object.entries(draft).filter(([k, v]) => {
		const orig = data?.fields.find(f => f.name === k)?.value;
		return String(v ?? '') !== String(orig ?? '');
	});

	const save = async () => {
		setSaving(true); setResult(null);
		try {
			const res = await api(`/api/edit/${resource}/${encodeURIComponent(id)}`, {
				method: 'PUT', headers: {'Content-Type': 'application/json'},
				body: JSON.stringify({changes: Object.fromEntries(changed)}),
			});
			const j = await res.json();
			if (!res.ok) { setResult({error: j.error}); return; }
			setResult({ok: j.changed});
			setDraft({});
			setData(d => ({...d, fields: d.fields.map(f =>
				(j.changed.includes(f.name) ? {...f, value: j.resource[f.name] ?? f.value} : f))}));
			onSaved?.();
		} catch (e) {
			setResult({error: e.message});
		} finally { setSaving(false); }
	};

	return (
		<div className="drawer" onClick={onClose}>
			<div className="drawer__panel" onClick={e => e.stopPropagation()}>
				<div className="drawer__head">
					<span className="drawer__title">Redigera {id}</span>
					<button className="btn drawer__close" onClick={onClose}>Stäng</button>
				</div>
				<div className="drawer__sub">Skrivs direkt till Fortnox-registret.</div>

				{error && <div className="panel"><b>Kan inte redigera</b><p>{error}</p></div>}

				{missing && (
					<div className="panel">
						<div className="panel__title">Finns inte i Fortnox ännu</div>
						<p>
							{missing.error}{' '}
							{missing.can_create
								? 'Den finns i det lokala registret och kan skapas i Fortnox med fälten nedan.'
								: 'Det finns ingen lokal post att skapa den ifrån.'}
						</p>
						{missing.would_send && (
							<dl className="kv" style={{marginTop: 10}}>
								{Object.entries(missing.would_send).map(([k, v]) => (
									<div key={k} style={{display: 'contents'}}>
										<dt>{LABELS[k] ?? k}</dt><dd>{String(v)}</dd>
									</div>
								))}
							</dl>
						)}
						{missing.can_create && (
							<div className="editbar" style={{marginTop: 10}}>
								<span className="spacer" />
								<button className="btn btn--primary" onClick={create} disabled={saving}>
									{saving ? 'Skapar…' : 'Skapa i Fortnox'}
								</button>
							</div>
						)}
						{result?.error && <p><b>Fortnox nekade:</b> {result.error}</p>}
						{result?.created && <p>Skapad som {result.created}.</p>}
					</div>
				)}

				{!data && !error && !missing && <div className="state">Hämtar…</div>}

				{data && (
					<>
						<div className="editgrid">
							{data.fields.map(f => (
								<label key={f.name} className="editfield">
									<span>{LABELS[f.name] ?? f.name}</span>
									<input className="search" style={{minWidth: 0, width: '100%'}}
										value={draft[f.name] ?? f.value ?? ''}
										onChange={e => setDraft(d => ({...d, [f.name]: e.target.value}))} />
								</label>
							))}
						</div>

						<div className="editbar">
							<span className="stamp">
								{changed.length
									? `${changed.length} ändrade: ${changed.map(([k]) => LABELS[k] ?? k).join(', ')}`
									: 'Inga ändringar'}
							</span>
							<span className="spacer" />
							<button className="btn" onClick={() => setDraft({})} disabled={!changed.length}>
								Återställ
							</button>
							{confirmOff ? (
								<>
									<button className="btn" onClick={() => setConfirmOff(false)}>Avbryt</button>
									<button className="btn btn--primary" onClick={deactivate} disabled={saving}>
										{saving ? 'Avaktiverar…' : 'Bekräfta avaktivering'}
									</button>
								</>
							) : (
								<button className="btn" onClick={() => setConfirmOff(true)} disabled={saving}
									title="Sätter Aktiv = nej i både Fortnox och Jabr. Raderar ingenting.">
									Ta bort
								</button>
							)}
							<button className="btn btn--primary" onClick={save}
								disabled={!changed.length || saving}>
								{saving ? 'Sparar…' : 'Spara till Fortnox'}
							</button>
						</div>

						{confirmOff && (
							<div className="panel">
								<div className="panel__title">Avaktivera {id}?</div>
								<p>
									Posten raderas inte. Aktiv sätts till nej i både Fortnox och Jabr, vilket
									går att ångra och lämnar historiken intakt — en hård radering vägras ändå
									av Fortnox för allt som en faktura hänvisar till.
								</p>
							</div>
						)}
						{result?.ok && <div className="panel"><b>Sparat</b>
							<p>{result.ok.join(', ')} uppdaterade i Fortnox och speglade lokalt.</p></div>}
						{result?.deactivated && <div className="panel"><b>Avaktiverad</b>
							<p>Aktiv = nej i både Fortnox och Jabr. Inget är raderat.</p></div>}
						{result?.error && <div className="panel"><b>Fortnox nekade</b>
							<p>{result.error}{result.queued && ' Ändringen ligger kvar lokalt och skickas vid nästa synk.'}</p></div>}

						{data.readonly && Object.keys(data.readonly).length > 0 && (
							<>
								<div className="md__title" style={{marginTop: 18}}>Fält Jabr inte skickar</div>
								<p className="stamp" style={{margin: '0 0 8px'}}>
									Inte nödvändigtvis skrivskyddade i Fortnox — de står bara utanför
									allow-listan i resources.js. Fortnox avvisar en PUT som innehåller ett
									fält som faktiskt är skrivskyddat, så listan har hållits snäv; vilka av
									de här som går att skriva är inte fastställt.
								</p>
								<dl className="kv">
									{Object.entries(data.readonly).map(([k, v]) => (
										<div key={k} style={{display: 'contents'}}>
											<dt>{k}</dt><dd>{String(v)}</dd>
										</div>
									))}
								</dl>
							</>
						)}
					</>
				)}
			</div>
		</div>
	);
}
